"use client";

import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Button } from "./Button";
import { Spinner } from "./Spinner";

// d2d-XDR eval 4.5 — the log-search table. /logs was a stack of hand-built
// row buttons with numbered pages: fifty rows at a time, a page click to see
// the next fifty, and no way to move through results from the keyboard. Log
// search wants the opposite shape:
//
//   WINDOWED   only the rows in view (plus a small overscan) are mounted, so
//              a result set of thousands scrolls at the cost of forty rows.
//              Fixed row height is what makes that exact and cheap.
//   CURSOR     results APPEND. `hasMore` + `onLoadMore` fetch the next batch
//              when the analyst nears the end (and a Load more button does
//              it explicitly) — no page count to know in advance, which a
//              stream of unknown length can't give.
//   TRIAGE     the list is one tab stop with a roving active row
//              (aria-activedescendant): j / ↓ and k / ↑ move, Enter or o
//              opens, Home / End jump. The active row scrolls into view.
//
// Sticky header, columns by fixed width with one flexible column. Selection
// of the open row is the caller's (`activeKey`), so the detail drawer and the
// table agree on which row is open.

export interface LogSearchColumn<Row> {
  key: string;
  header: ReactNode;
  /** CSS width ("160px"). Omit on exactly one column to let it take the rest. */
  width?: string;
  cell: (row: Row, index: number) => ReactNode;
  align?: "start" | "end";
}

export interface LogSearchTableProps<Row> {
  rows: Row[];
  columns: LogSearchColumn<Row>[];
  rowKey: (row: Row, index: number) => string;
  /** Row height in px. Fixed — it is what makes windowing exact. */
  rowHeight?: number;
  /** Height of the scrolling viewport. */
  height?: number | string;
  onRowOpen?: (row: Row, index: number) => void;
  /** Key of the row shown as open (e.g. in a detail drawer). */
  activeKey?: string | null;
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Total matching rows, when the backend knows it. */
  total?: number;
  empty?: ReactNode;
  "aria-label": string;
  className?: string;
}

const OVERSCAN = 8;

export function LogSearchTable<Row>({
  rows, columns, rowKey, rowHeight = 36, height = "62vh", onRowOpen, activeKey,
  loading, hasMore, loadingMore, onLoadMore, total, empty, className, "aria-label": ariaLabel,
}: LogSearchTableProps<Row>) {
  const baseId = useId();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setViewportH(el.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep the roving cursor inside the list as rows change (a new search).
  useEffect(() => {
    if (cursor > rows.length - 1) setCursor(Math.max(0, rows.length - 1));
  }, [rows.length, cursor]);

  // Near the end: ask for the next batch.
  useEffect(() => {
    if (!hasMore || loadingMore || !onLoadMore || rows.length === 0) return;
    const lastVisible = Math.floor((scrollTop + viewportH) / rowHeight);
    if (lastVisible >= rows.length - OVERSCAN) onLoadMore();
  }, [scrollTop, viewportH, rowHeight, rows.length, hasMore, loadingMore, onLoadMore]);

  const scrollIntoView = useCallback((index: number) => {
    const el = viewportRef.current;
    if (!el) return;
    const top = index * rowHeight;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + rowHeight > el.scrollTop + el.clientHeight) el.scrollTop = top + rowHeight - el.clientHeight;
  }, [rowHeight]);

  const move = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(rows.length - 1, next));
    setCursor(clamped);
    scrollIntoView(clamped);
  }, [rows.length, scrollIntoView]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (rows.length === 0 || e.altKey || e.ctrlKey || e.metaKey) return;
    const page = Math.max(1, Math.floor(viewportH / rowHeight) - 1);
    const keys: Record<string, () => void> = {
      ArrowDown: () => move(cursor + 1), j: () => move(cursor + 1),
      ArrowUp: () => move(cursor - 1), k: () => move(cursor - 1),
      PageDown: () => move(cursor + page), PageUp: () => move(cursor - page),
      Home: () => move(0), End: () => move(rows.length - 1),
      Enter: () => onRowOpen?.(rows[cursor], cursor), o: () => onRowOpen?.(rows[cursor], cursor),
    };
    const fn = keys[e.key];
    if (fn) { e.preventDefault(); fn(); }
  };

  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
  const end = Math.min(rows.length, Math.ceil((scrollTop + viewportH) / rowHeight) + OVERSCAN);
  const template = columns.map((c) => c.width ?? "minmax(0, 1fr)").join(" ");
  const rowId = (i: number) => `${baseId}-row-${i}`;

  return (
    <div className={cn("rc-logtable", className)} style={{ "--rc-logtable-cols": template, "--rc-logtable-row": `${rowHeight}px` } as CSSProperties}>
      <div className="rc-logtable__head" role="row">
        {columns.map((c) => (
          <div key={c.key} role="columnheader" className="rc-logtable__th" data-align={c.align}>{c.header}</div>
        ))}
      </div>
      <div
        ref={viewportRef}
        className="rc-logtable__viewport"
        style={{ height }}
        role="grid"
        aria-label={ariaLabel}
        aria-rowcount={hasMore ? -1 : rows.length}
        aria-busy={loading || loadingMore || undefined}
        aria-activedescendant={rows.length ? rowId(cursor) : undefined}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        {loading && rows.length === 0 ? (
          <div className="rc-logtable__state"><Spinner size="16px" /> Searching…</div>
        ) : rows.length === 0 ? (
          <div className="rc-logtable__state">{empty ?? "No results"}</div>
        ) : (
          <div className="rc-logtable__body" style={{ height: rows.length * rowHeight }}>
            {rows.slice(start, end).map((row, offset) => {
              const i = start + offset;
              const key = rowKey(row, i);
              return (
                <div
                  key={key}
                  id={rowId(i)}
                  role="row"
                  aria-rowindex={i + 1}
                  aria-selected={activeKey === key || undefined}
                  data-cursor={i === cursor || undefined}
                  className="rc-logtable__row"
                  style={{ transform: `translateY(${i * rowHeight}px)` }}
                  onClick={() => { setCursor(i); onRowOpen?.(row, i); }}
                >
                  {columns.map((c) => (
                    <div key={c.key} role="gridcell" className="rc-logtable__td" data-align={c.align}>{c.cell(row, i)}</div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="rc-logtable__foot">
        <span>
          {rows.length.toLocaleString()} loaded{typeof total === "number" ? ` of ${total.toLocaleString()}` : ""}
          <span className="rc-logtable__keys" aria-hidden> · j/k to move · Enter to open</span>
        </span>
        {hasMore ? (
          <Button variant="secondary" size="sm" onClick={onLoadMore} loading={loadingMore} disabled={loadingMore}>
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  );
}
